/**
 * ProviderPicker — presentational single-provider destination picker.
 *
 * One provider's section: heading (logo + name + account), then either a Connect
 * CTA or a single-provider combobox (Popover + cmdk). Selected chips are scoped
 * to this provider, so there's never any ambiguity about which platform a
 * destination belongs to. Fully props-driven — it holds no fetch/state logic, so
 * the Slack and Google Chat sections of `RoutineChannelPicker` both render this
 * one control (chips, Slack invite, stale rows) without duplicating it.
 *
 * Intentionally NOT built on the shared `MultiSelectV2` (ui-core): that control
 * is uncontrolled and has no per-row `selectable` gating (Slack channels the bot
 * hasn't joined yet) or per-row trailing slot (Invite button / stale badge) —
 * the three things this picker needs. Its chip/tick styling is deliberately kept
 * aligned with `MultiSelectV2`; keep the two visually in sync if either changes.
 */

import { useMemo, type ReactNode } from 'react';
import { Check, ChevronDown, Loader2, Plus } from 'lucide-react';
import {
  Badge,
  Skeleton,
  cn,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@strivelabs/ui-core';
import { Button } from '@strivelabs/ui-core/v2';
import { AppLogo } from '@/components/AppLogo';
import type { ConnectionsCardData } from '@/types/connections';

/** A single selectable row in a provider's picker, keyed on its stable id. */
export interface PickerRow {
  /** Stable identifier — Slack channel id / Google Chat space resource name. */
  value: string;
  /** Display label, e.g. "# general" or a space's displayName. */
  label: string;
  /** Slack channels the bot hasn't joined can't be selected until invited. */
  selectable: boolean;
  /** Trailing affordance: Slack Invite button, GChat space-type badge, or stale tag. */
  trailing?: ReactNode;
}

/** A selected destination shown as a chip in the trigger. */
export interface SelectedChip {
  value: string;
  label: string;
  /** Label still resolving from the in-flight fetch — render a shimmer, not a raw id. */
  loading: boolean;
}

export const credLabel = (cred?: ConnectionsCardData): string => {
  if (!cred) return '';
  const principal = cred.principal_name || cred.principal_email;
  return principal ? `${cred.display_name} · ${principal}` : cred.display_name;
};

/** Selection checkbox tick, mirroring the shared MultiSelect styling. */
const Tick = ({ checked }: { checked: boolean }) => (
  <div
    className={cn(
      'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-gray-400 flex-shrink-0',
      checked ? 'bg-black text-white' : 'bg-white [&_svg]:invisible'
    )}
  >
    <Check className="h-3.5 w-3.5" />
  </div>
);

export const staleBadge = (text: string) => (
  <span className="text-[10px] uppercase tracking-wide text-amber-600">{text}</span>
);

/** How many selected chips to show in a trigger before collapsing to "+N more". */
const MAX_CHIPS = 4;

export interface DestinationComboboxProps {
  appName: string;
  /** Plural noun for placeholder/empty/count, e.g. "channels" / "spaces". */
  noun: string;
  isLoading: boolean;
  rows: PickerRow[];
  selected: SelectedChip[];
  onToggle: (value: string) => void;
  /** Locks the trigger (e.g. a submitted in-chat card). */
  disabled?: boolean;
}

/**
 * The multi-select combobox (chips trigger + searchable checkbox list) shared
 * by `ProviderPicker` and the in-chat `ui_channel_input` card. Kept visually
 * aligned with `MultiSelectV2` — see the header note for why it's not built on
 * it.
 */
export const DestinationCombobox = ({
  appName,
  noun,
  isLoading,
  rows,
  selected,
  onToggle,
  disabled = false,
}: DestinationComboboxProps) => {
  const selectedSet = useMemo(() => new Set(selected.map((s) => s.value)), [selected]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className="flex w-full min-h-9 h-auto items-center justify-between gap-2 rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-900 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:bg-gray-100 disabled:opacity-50"
        >
          {selected.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1 min-w-0 flex-1 pr-2">
              {selected.slice(0, MAX_CHIPS).map((chip) => (
                <Badge
                  key={chip.value}
                  className="max-w-full truncate bg-gray-100 text-gray-800 border-gray-300 hover:bg-gray-200"
                >
                  {chip.loading ? (
                    <Skeleton className="h-3.5 w-16" />
                  ) : (
                    <span className="truncate">{chip.label}</span>
                  )}
                </Badge>
              ))}
              {selected.length > MAX_CHIPS && (
                <Badge className="bg-gray-100 text-gray-800 border-gray-300 hover:bg-gray-200">
                  +{selected.length - MAX_CHIPS} more
                </Badge>
              )}
            </div>
          ) : isLoading ? (
            <span className="mx-1 flex items-center gap-1.5 truncate text-sm text-gray-500">
              <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
              Loading {noun}...
            </span>
          ) : (
            <span className="mx-1 truncate text-sm text-gray-500">Select {noun}</span>
          )}
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 text-gray-500" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] min-w-[320px] p-0"
      >
        <Command className="bg-white">
          <CommandInput
            placeholder={`Search ${noun}...`}
            className="text-gray-700 placeholder:text-gray-500"
          />
          <CommandList className="max-h-[300px]">
            <CommandEmpty className="text-gray-500">No {noun} found.</CommandEmpty>
            <CommandGroup>
              {isLoading && rows.length === 0 ? (
                <CommandItem disabled value={`${appName}-loading`} className="text-gray-500">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading {noun}...
                </CommandItem>
              ) : rows.length === 0 ? (
                <CommandItem disabled value={`${appName}-empty`} className="text-gray-500">
                  No {noun} found
                </CommandItem>
              ) : (
                rows.map((row) => (
                  <CommandItem
                    key={row.value}
                    value={`${row.label} ${row.value}`}
                    onSelect={() => {
                      if (row.selectable) onToggle(row.value);
                    }}
                    className={cn(
                      'text-gray-900',
                      row.selectable ? 'cursor-pointer' : 'cursor-default opacity-70'
                    )}
                  >
                    <div className="flex w-full items-center justify-between gap-2">
                      <div className="flex min-w-0 items-center">
                        <Tick checked={selectedSet.has(row.value)} />
                        <span className="truncate">{row.label}</span>
                      </div>
                      {row.trailing}
                    </div>
                  </CommandItem>
                ))
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export interface ProviderPickerProps {
  appName: 'slack' | 'google_chat';
  displayName: string;
  /** Plural noun for placeholder/empty/count, e.g. "channels" / "spaces". */
  noun: string;
  account?: ConnectionsCardData;
  connected: boolean;
  isLoading: boolean;
  rows: PickerRow[];
  selected: SelectedChip[];
  onToggle: (value: string) => void;
  onConnect: () => void;
  /** Provider-specific helper line under the picker. */
  helper: ReactNode;
}

export const ProviderPicker = ({
  appName,
  displayName,
  noun,
  account,
  connected,
  isLoading,
  rows,
  selected,
  onToggle,
  onConnect,
  helper,
}: ProviderPickerProps) => {
  const singular = noun.replace(/s$/, '');

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2 text-sm">
        <AppLogo shortName={appName} displayName={displayName} size="sm" />
        <span className="font-medium text-gray-700">{displayName}</span>
        {account && <span className="truncate text-gray-400">· {credLabel(account)}</span>}
      </div>

      {!connected ? (
        <Button variant="secondary" size="sm" icon={<Plus />} onClick={onConnect}>
          Connect {displayName} to send routine alerts
        </Button>
      ) : (
        <>
          <DestinationCombobox
            appName={appName}
            noun={noun}
            isLoading={isLoading}
            rows={rows}
            selected={selected}
            onToggle={onToggle}
          />

          {helper}
          {selected.length > 0 && (
            <p className="text-xs text-primary-600">
              {selected.length} {selected.length === 1 ? singular : noun} selected
            </p>
          )}
        </>
      )}
    </div>
  );
};
